import { Suspense } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import Index from "./pages/Index";
import { lazyWithRefresh } from "@/lib/lazyWithRefresh";

const Display = lazyWithRefresh(() => import("./pages/Display"));
const NotFound = lazyWithRefresh(() => import("./pages/NotFound"));
const OrganizerLayout = lazyWithRefresh(() => import("./pages/OrganizerLayout"));
const OrganizerDashboard = lazyWithRefresh(() => import("./pages/OrganizerDashboard"));
const OrganizerEvent = lazyWithRefresh(() => import("./pages/OrganizerEvent"));
const OrganizerEventSettings = lazyWithRefresh(() => import("./pages/OrganizerEventSettings"));
const OrganizerEventDivisions = lazyWithRefresh(() => import("./pages/OrganizerEventDivisions"));
const OrganizerEventSchedule = lazyWithRefresh(() => import("./pages/OrganizerEventSchedule"));
const OrganizerEventLiveControl = lazyWithRefresh(() => import("./pages/OrganizerEventLiveControl"));
const OrganizerEventPublishing = lazyWithRefresh(() => import("./pages/OrganizerEventPublishing"));
const OrganizerEventExtras = lazyWithRefresh(() => import("./pages/OrganizerEventExtras"));
const OrganizerNewEvent = lazyWithRefresh(() => import("./pages/OrganizerNewEvent"));
const QuickEventWizard = lazyWithRefresh(() => import("./pages/QuickEventWizard"));
const PersistentTournamentWizard = lazyWithRefresh(() => import("./pages/PersistentTournamentWizard"));
const CommunityCupWizard = lazyWithRefresh(() => import("./pages/CommunityCupWizard"));
const BuilderWizardPage = lazyWithRefresh(() => import("./pages/BuilderWizardPage"));
const BuilderLiveControlPage = lazyWithRefresh(() => import("./pages/BuilderLiveControlPage"));
const BuilderDisplayPage = lazyWithRefresh(() => import("./pages/BuilderDisplayPage"));
const BuilderPrintPage = lazyWithRefresh(() => import("./pages/BuilderPrintPage"));
const CommunityCupLiveControl = lazyWithRefresh(() => import("./pages/CommunityCupLiveControl"));
const CommunityCupJokersPage = lazyWithRefresh(() => import("./pages/CommunityCupJokersPage"));
const CommunityCupRosterPage = lazyWithRefresh(() => import("./pages/CommunityCupRosterPage"));
const CommunityCupSchedulePage = lazyWithRefresh(() => import("./pages/CommunityCupSchedulePage"));
const CommunityCupPublicView = lazyWithRefresh(() => import("./pages/CommunityCupPublicView"));
const CommunityCupDisplay = lazyWithRefresh(() => import("./pages/CommunityCupDisplay"));
const CommunityCupOverviewDisplay = lazyWithRefresh(() => import("./pages/CommunityCupOverviewDisplay"));
const CommunityCupCourtView = lazyWithRefresh(() => import("./pages/CommunityCupCourtView"));
const ClashSignupPage = lazyWithRefresh(() => import("./pages/ClashSignupPage"));
const PublicEventPage = lazyWithRefresh(() => import("./pages/PublicEventPage"));
const LiveEventDisplayPage = lazyWithRefresh(() => import("./pages/LiveEventDisplayPage"));
const EventsIntelligenceHub = lazyWithRefresh(() => import("./pages/EventsIntelligenceHub"));
const EventIntelligenceDetail = lazyWithRefresh(() => import("./pages/EventIntelligenceDetail"));
const MatchTracker = lazyWithRefresh(() => import("./pages/MatchTracker"));
const PerformanceMethodology = lazyWithRefresh(() => import("./pages/PerformanceMethodology"));
const BracketTemplateBuilder = lazyWithRefresh(() => import("./pages/BracketTemplateBuilder"));
const TemplatePreviewDisplay = lazyWithRefresh(() => import("./pages/TemplatePreviewDisplay"));
const PlayerMatchHistory = lazyWithRefresh(() => import("./pages/PlayerMatchHistory"));
const PlayerDive = lazyWithRefresh(() => import("./pages/PlayerDive"));
const PlayerLeaderboard = lazyWithRefresh(() => import("./pages/PlayerLeaderboard"));
const PlayerPartnerships = lazyWithRefresh(() => import("./pages/PlayerPartnerships"));
const EloExplained = lazyWithRefresh(() => import("./pages/EloExplained"));
const CommunityManager = lazyWithRefresh(() => import("./pages/CommunityManager"));
const InterleagueAdmin = lazyWithRefresh(() => import("./pages/InterleagueAdmin"));
const InterleaguePublicPage = lazyWithRefresh(() => import("./pages/InterleaguePublicPage"));
const InterleagueInternal = lazyWithRefresh(() => import("./pages/InterleagueInternal"));
const AdminHub = lazyWithRefresh(() => import("./pages/AdminHub"));
const AdminPlayerHub = lazyWithRefresh(() => import("./pages/AdminPlayerHub"));
const AdminPadelProposals = lazyWithRefresh(() => import("./pages/AdminPadelProposals"));
const AdminLadders = lazyWithRefresh(() => import("./pages/AdminLadders"));
const AdminLadderWorkspace = lazyWithRefresh(() => import("./pages/AdminLadderWorkspace"));
const AdminCrm = lazyWithRefresh(() => import("./pages/AdminCrm"));
const AdminCrmContact = lazyWithRefresh(() => import("./pages/AdminCrmContact"));
const LadderSeasonPage = lazyWithRefresh(() => import("./pages/LadderSeasonPage"));
const LadderMatchPage = lazyWithRefresh(() => import("./pages/LadderMatchPage"));
const LadderFindTimePage = lazyWithRefresh(() => import("./pages/LadderFindTimePage"));
const SkipWeekPage = lazyWithRefresh(() => import("./pages/SkipWeekPage"));
const LadderRegisterPage = lazyWithRefresh(() => import("./pages/LadderRegisterPage"));
const LadderRulesPage = lazyWithRefresh(() => import("./pages/LadderRulesPage"));
const LadderInfoPage = lazyWithRefresh(() => import("./pages/LadderInfoPage"));
const UpcomingEventsPage = lazyWithRefresh(() => import("./pages/UpcomingEventsPage"));
const UpcomingEventPage = lazyWithRefresh(() => import("./pages/UpcomingEventPage"));
const OrganizerUpcomingListPage = lazyWithRefresh(() => import("./pages/OrganizerUpcomingListPage"));
const CreateUpcomingEventPage = lazyWithRefresh(() => import("./pages/CreateUpcomingEventPage"));
const ManageUpcomingEventPage = lazyWithRefresh(() => import("./pages/ManageUpcomingEventPage"));
const Home = lazyWithRefresh(() => import("./pages/Home"));
const HomeStats = lazyWithRefresh(() => import("./pages/HomeStats"));
const Welcome = lazyWithRefresh(() => import("./pages/Welcome"));
const CheckEmail = lazyWithRefresh(() => import("./pages/auth/CheckEmail"));
const AuthCallback = lazyWithRefresh(() => import("./pages/auth/AuthCallback"));
const Terms = lazyWithRefresh(() => import("./pages/legal/Terms"));
const Privacy = lazyWithRefresh(() => import("./pages/legal/Privacy"));
import { OrganizerEventsProvider } from "./context/OrganizerEventsContext";
import { PlayerProfileSheetProvider } from "./components/player-profile/PlayerProfileSheetContext";
import { SignedInTabBar } from "./components/nav/SignedInTabBar";
import { AuthProvider } from "./context/AuthContext";
import { RouteTracker } from "./components/RouteTracker";
import { LadderMemberGate } from "./components/ladder/portal/LadderMemberGate";
const OrganizerLogin = lazyWithRefresh(() => import("./pages/OrganizerLogin"));
const Login = lazyWithRefresh(() => import("./pages/Login"));
const InviteLanding = lazyWithRefresh(() => import("./pages/InviteLanding"));
const PlayerClaim = lazyWithRefresh(() => import("./pages/PlayerClaim"));
const PlayerProfile = lazyWithRefresh(() => import("./pages/PlayerProfile"));
const PlayerSettings = lazyWithRefresh(() => import("./pages/PlayerSettings"));
const CaptainEventPage = lazyWithRefresh(() => import("./pages/CaptainEventPage"));
const ScheduleProposalTaoTcc = lazyWithRefresh(() => import("./pages/ScheduleProposalTaoTcc"));
const CommunitiesListPage = lazyWithRefresh(() => import("./pages/organizer/CommunitiesListPage"));
const CommunitySetupWizard = lazyWithRefresh(() => import("./pages/organizer/CommunitySetupWizard"));
const CommunityPanelPage = lazyWithRefresh(() => import("./pages/organizer/CommunityPanelPage"));
const NightRunnerPage = lazyWithRefresh(() => import("./pages/organizer/NightRunnerPage"));
const CommunityHomePage = lazyWithRefresh(() => import("./pages/community/CommunityHomePage"));
const CommunityStandingsPage = lazyWithRefresh(() => import("./pages/community/CommunityStandingsPage"));
const NightPublicPage = lazyWithRefresh(() => import("./pages/community/NightPublicPage"));
const NightDisplayPage = lazyWithRefresh(() => import("./pages/community/NightDisplayPage"));

const queryClient = new QueryClient();

/** Old division and standings URLs land on the season page (spec 2026-10-07 §3). */
const LadderDivisionRedirect = () => {
  const { slug = "", divisionId = "" } = useParams<{ slug: string; divisionId: string }>();
  return <Navigate to={`/ladders/${slug}?d=${divisionId}`} replace />;
};
const LadderStandingsRedirect = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  return <Navigate to={`/ladders/${slug}`} replace />;
};
/** My week used to be its own page; notification deep links still carry /availability?week=... */
const LadderAvailabilityRedirect = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const [sp] = useSearchParams();
  const next = new URLSearchParams(sp);
  next.set("tab", "week");
  return <Navigate to={`/ladders/${slug}?${next.toString()}`} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
      <OrganizerEventsProvider>
        <BrowserRouter>
          <RouteTracker />
          <PlayerProfileSheetProvider>
          <Sentry.ErrorBoundary fallback={
            <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-sm text-muted-foreground">
              <p>Something went wrong. Please reload the page.</p>
              <button className="rounded border px-3 py-1.5 text-xs hover:bg-muted" onClick={() => window.location.reload()}>Reload</button>
            </div>
          }>
          <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">Loading…</div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/match-tracker" element={<MatchTracker />} />
              <Route path="/performance-methodology" element={<PerformanceMethodology />} />
              <Route path="/players/history" element={<PlayerMatchHistory />} />
              <Route path="/players/dive" element={<PlayerDive />} />
              <Route path="/players/dive/:communitySlug" element={<PlayerDive />} />
              <Route path="/players/leaderboard" element={<PlayerLeaderboard />} />
              <Route path="/players/partnerships" element={<PlayerPartnerships />} />
              <Route path="/players/elo-explained" element={<EloExplained />} />
              <Route path="/interleague/results" element={<InterleaguePublicPage />} />
              <Route path="/interleague/results/:seasonId" element={<InterleaguePublicPage />} />
              <Route path="/interleague/results/:seasonId/:divisionId" element={<InterleaguePublicPage />} />
              <Route path="/interleague/admin" element={<InterleagueAdmin />} />
              <Route path="/interleague/internal" element={<InterleagueInternal />} />
              <Route path="/interleague/internal/:seasonId" element={<InterleagueInternal />} />
              <Route path="/interleague/internal/:seasonId/:divisionId" element={<InterleagueInternal />} />
              <Route path="/admin" element={<AdminHub />} />
              <Route path="/admin/player-hub" element={<AdminPlayerHub />} />
              <Route path="/admin/padel-proposals" element={<AdminPadelProposals />} />
              <Route path="/admin/ladders" element={<AdminLadders />} />
              <Route path="/admin/ladders/:ladderId" element={<AdminLadderWorkspace />} />
              <Route path="/admin/crm" element={<AdminCrm />} />
              <Route path="/admin/crm/:contactId" element={<AdminCrmContact />} />
              <Route path="/ladders/:slug" element={<LadderSeasonPage />} />
              <Route path="/ladders/:slug/availability" element={<LadderAvailabilityRedirect />} />
              <Route path="/ladders/:slug/m/:fixtureId" element={<LadderMemberGate><LadderMatchPage /></LadderMemberGate>} />
              <Route path="/ladders/:slug/m/:fixtureId/find" element={<LadderMemberGate><LadderFindTimePage /></LadderMemberGate>} />
              <Route path="/ladders/:slug/d/:divisionId" element={<LadderDivisionRedirect />} />
              <Route path="/skip-week" element={<SkipWeekPage />} />
              <Route path="/ladders/:slug/register" element={<LadderRegisterPage />} />
              <Route path="/ladders/:slug/rules" element={<LadderRulesPage />} />
              <Route path="/ladders/:slug/info" element={<LadderInfoPage />} />
              <Route path="/ladders/:slug/standings" element={<LadderStandingsRedirect />} />
              <Route path="/communities/manage" element={<CommunityManager />} />
              <Route path="/display" element={<Display />} />
              <Route path="/events/intelligence" element={<EventsIntelligenceHub />} />
              <Route path="/events/intelligence/:eventKey" element={<EventIntelligenceDetail />} />
              <Route path="/events/:slug" element={<PublicEventPage />} />
              <Route path="/events/:slug/live" element={<LiveEventDisplayPage />} />
              <Route path="/events/:slug/court/:courtNo" element={<CommunityCupCourtView />} />
              <Route path="/events/:slug/clash-signup/:side" element={<ClashSignupPage />} />
              <Route path="/upcoming" element={<UpcomingEventsPage />} />
              <Route path="/upcoming/:slug" element={<UpcomingEventPage />} />
              <Route path="/community-cup/display" element={<CommunityCupDisplay />} />
              <Route path="/builder/display" element={<BuilderDisplayPage />} />
              <Route path="/builder/print" element={<BuilderPrintPage />} />
              <Route path="/community-cup/overview" element={<CommunityCupOverviewDisplay />} />
              <Route path="/captain/:eventId" element={<CaptainEventPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/login/check-email" element={<CheckEmail />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              {/* Carry the organizer destination through, so a bookmarked
                  /organizer/login still lands in the dashboard after sign-in
                  instead of falling through to /claim. */}
              <Route
                path="/organizer/login"
                element={<Navigate to="/login" state={{ from: { pathname: "/organizer" } }} replace />}
              />
              <Route path="/invite/:token" element={<InviteLanding />} />
              <Route path="/claim" element={<PlayerClaim />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/profile" element={<PlayerProfile />} />
              <Route path="/profile/settings" element={<PlayerSettings />} />
              <Route path="/home" element={<Home />} />
              <Route path="/home/stats" element={<HomeStats />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/organizer" element={<OrganizerLayout />}>
                <Route index element={<OrganizerDashboard />} />
                <Route path="communities" element={<CommunitiesListPage />} />
                <Route path="communities/new" element={<CommunitySetupWizard />} />
                <Route path="communities/:slug" element={<CommunityPanelPage />} />
                <Route path="communities/:slug/nights/:nightId" element={<NightRunnerPage />} />
                <Route path="upcoming" element={<OrganizerUpcomingListPage />} />
                <Route path="upcoming/new" element={<CreateUpcomingEventPage />} />
                <Route path="upcoming/:slug/manage" element={<ManageUpcomingEventPage />} />
                <Route path="events/new" element={<OrganizerNewEvent />} />
                <Route path="events/new/quick" element={<QuickEventWizard />} />
                <Route path="events/new/tournament" element={<PersistentTournamentWizard />} />
                <Route path="events/new/community-cup" element={<CommunityCupWizard />} />
                <Route path="events/new/builder" element={<BuilderWizardPage />} />
                <Route path="template-builder" element={<BracketTemplateBuilder />} />
                <Route path="template-builder/preview" element={<TemplatePreviewDisplay />} />
                <Route path="template-builder/preview/:templateId" element={<TemplatePreviewDisplay />} />
                <Route path="events/:eventId" element={<OrganizerEvent />}>
                  <Route index element={<OrganizerEventSettings />} />
                  <Route path="divisions" element={<OrganizerEventDivisions />} />
                  <Route path="schedule" element={<OrganizerEventSchedule />} />
                  <Route path="live-control" element={<OrganizerEventLiveControl />} />
                  <Route path="publishing" element={<OrganizerEventPublishing />} />
                  <Route path="extras" element={<OrganizerEventExtras />} />
                  <Route path="community-cup" element={<CommunityCupLiveControl />} />
                  <Route path="cc-jokers" element={<CommunityCupJokersPage />} />
                  <Route path="cc-roster" element={<CommunityCupRosterPage />} />
                  <Route path="cc-schedule" element={<CommunityCupSchedulePage />} />
                  <Route path="builder" element={<BuilderLiveControlPage />} />
                  <Route path="builder/setup" element={<BuilderWizardPage />} />
                </Route>
              </Route>
              <Route path="/schedule-proposal" element={<ScheduleProposalTaoTcc />} />
              <Route path="/c/:slug" element={<CommunityHomePage />} />
              <Route path="/c/:slug/standings" element={<CommunityStandingsPage />} />
              <Route path="/c/:slug/nights/:nightId" element={<NightPublicPage />} />
              <Route path="/c/:slug/nights/:nightId/display" element={<NightDisplayPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <SignedInTabBar />
          </Sentry.ErrorBoundary>
          </PlayerProfileSheetProvider>
        </BrowserRouter>
      </OrganizerEventsProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
